import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  
  // Store scroll position before opening menu
  #savedScrollPosition = 0;
  
  /**
   * Recompute global scroll-lock based on any open dialog/details that request it.
   * This prevents the page getting "stuck" unscrollable if a close path doesn't
   * emit a native `toggle` event (or if multiple overlays are involved).
   */
  #syncScrollLock() {
    const hasAnyOpenScrollLock = Boolean(document.querySelector('dialog[open][scroll-lock], details[open][scroll-lock]'));
    if (hasAnyOpenScrollLock) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    
    // Listen for toggle event on details element to handle scroll behavior
    // This fires after the menu state changes, so it doesn't interfere with the click
    this.refs.details.addEventListener('toggle', this.#onDetailsToggle);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onDetailsToggle);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Handle toggle event on details element to manage scroll behavior when menu opens/closes
   * @param {Event} event
   */
  #onDetailsToggle = (event) => {
    // Only handle scroll behavior for the main menu drawer on mobile
    if (window.innerWidth > 1199) return; // Desktop, no scroll handling needed
    
    const details = event.target;
    const isNowOpen = details.hasAttribute('open');
    
    if (isNowOpen) {
      // Menu just opened: save scroll position and scroll to top
      this.#savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      
      // Scroll immediately (instant, not smooth)
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Reinforce scroll after any pending DOM updates
      requestAnimationFrame(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        
        // Force scroll again in the next frame to ensure it sticks
        requestAnimationFrame(() => {
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        });
      });
    } else {
      // Menu just closed: restore scroll position
      if (this.#savedScrollPosition > 0) {
        const savedPos = this.#savedScrollPosition;
        this.#savedScrollPosition = 0; // Reset immediately to prevent double-restore
        
        // Scroll immediately (instant, not smooth)
        document.documentElement.scrollTop = savedPos;
        document.body.scrollTop = savedPos;
        window.scrollTo({ top: savedPos, left: 0, behavior: 'instant' });
        
        // Reinforce scroll after any pending DOM updates
        requestAnimationFrame(() => {
          document.documentElement.scrollTop = savedPos;
          document.body.scrollTop = savedPos;
          window.scrollTo({ top: savedPos, left: 0, behavior: 'instant' });
          
          // Force scroll again in the next frame to ensure it sticks
          requestAnimationFrame(() => {
            document.documentElement.scrollTop = savedPos;
            document.body.scrollTop = savedPos;
            window.scrollTo({ top: savedPos, left: 0, behavior: 'instant' });
          });
        });
      }
    }
  };

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        // Ensure scrolling is restored after closing the drawer
        this.#syncScrollLock();
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  // Prefer the property so the browser updates state consistently and emits `toggle` where applicable
  if (element.open) element.open = false;
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
